import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TooltipItem,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { Line } from 'react-chartjs-2';
import { dateAwareTickCallback, timeScaleMinUnit } from '../../../../utils/monitoringChartUtils';
import { useHistoricalPlaybackStore } from '../../../../store/historicalPlaybackStore';
import {
  fetchElementSeries,
  fetchMeasuredTrafoSeries,
  fetchSnapshotRange,
  type ElementSeriesPoint,
  type MeasuredTrafoSeriesPoint,
} from '../../../../services/elementHistoryService';
import { parseUtcTimestampMs } from '../../../../utils/timeUtils';
import { PU_BASE_LN_V } from '../../../../utils/electricalConstants';
import { VOLTAGE_OPERATIONAL_MIN_V, VOLTAGE_OPERATIONAL_MAX_V } from '../../../../utils/voltageConstants';
import { fmt } from '../../../../utils/numberFormat';

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface Props {
  isOpen: boolean;
  elementId: string;
  elementName: string;
  /** Show the loading chart (trafos/cables carry loadingPercent). */
  showLoading?: boolean;
  /** Show the voltage chart with the EN 50160 band markers (voltageConstants.ts). */
  showVoltage?: boolean;
  /** Also fetch/show the MEASURED trafo series (frequency, U, I). */
  showMeasuredTrafo?: boolean;
  onClose: () => void;
}

type RangeKey = '7d' | '30d' | '90d' | '365d' | 'all';

const RANGE_LABELS: Record<RangeKey, string> = {
  '7d': '7 Tage',
  '30d': '30 Tage',
  '90d': '90 Tage',
  '365d': '1 Jahr',
  all: 'Gesamt',
};

const RANGE_DAYS: Record<Exclude<RangeKey, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

interface BandSeries {
  label: string;
  unit: string;
  min: (number | null)[];
  avg: (number | null)[];
  max: (number | null)[];
  color: { border: string; bg: string };
  /** Dashed horizontal marker lines (e.g. the EN 50160 voltage band). */
  limits?: Array<{ value: number; label: string }>;
}

/**
 * Vertical marker at the instant currently selected on the map time-rewind —
 * ties the history chart to the point the map is showing.
 */
const makeMarkerPlugin = (markerMs: number | null) => ({
  id: 'historyInstantMarker',
  afterDatasetsDraw(chart: any) {
    if (markerMs == null) return;
    const x = chart.scales?.x;
    if (!x || markerMs < x.min || markerMs > x.max) return;
    const px = x.getPixelForValue(markerMs);
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = 'rgba(249, 115, 22, 0.9)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(px, top);
    ctx.lineTo(px, bottom);
    ctx.stroke();
    ctx.restore();
  },
});

const BandChart: React.FC<{ title: string; timestamps: string[]; series: BandSeries; markerMs?: number | null }> = ({ title, timestamps, series, markerMs = null }) => {
  const xs = useMemo(() => timestamps.map(t => parseUtcTimestampMs(t)), [timestamps]);
  const minUnit = useMemo(() => timeScaleMinUnit(timestamps), [timestamps]);
  const markerPlugin = useMemo(() => makeMarkerPlugin(markerMs), [markerMs]);
  const data = useMemo(() => {
    const toPoints = (values: (number | null)[]) => values.map((y, i) => ({ x: xs[i], y }));
    const datasets: any[] = [
      {
        label: `${series.label} (Min)`,
        data: toPoints(series.min),
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        pointRadius: 0,
        borderWidth: 0,
        fill: false,
        isBand: true,
      },
      {
        label: `${series.label} (Max)`,
        data: toPoints(series.max),
        borderColor: 'transparent',
        backgroundColor: series.color.bg,
        pointRadius: 0,
        borderWidth: 0,
        fill: { target: 0, above: series.color.bg },
        isBand: true,
      },
      {
        label: series.label,
        data: toPoints(series.avg),
        borderColor: series.color.border,
        backgroundColor: series.color.border,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        fill: false,
      },
    ];
    for (const limit of series.limits ?? []) {
      datasets.push({
        label: limit.label,
        data: xs.length > 0 ? [{ x: xs[0], y: limit.value }, { x: xs[xs.length - 1], y: limit.value }] : [],
        borderColor: 'rgba(239, 68, 68, 0.7)',
        backgroundColor: 'transparent',
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [6, 4],
        fill: false,
        isBand: true,
      });
    }
    return { datasets };
  }, [xs, series]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          filter: (item: TooltipItem<'line'>) => !(item.dataset as { isBand?: boolean }).isBand,
          callbacks: {
            label: (context: TooltipItem<'line'>) => {
              const avg = context.parsed.y;
              const min = data.datasets[0]?.data[context.dataIndex]?.y;
              const max = data.datasets[1]?.data[context.dataIndex]?.y;
              if (min != null && max != null && min !== max) {
                return `${series.label}: ${fmt((avg ?? 0), 2)} ${series.unit} (Min ${fmt(Number(min), 2)} | Max ${fmt(Number(max), 2)})`;
              }
              return `${series.label}: ${fmt((avg ?? 0), 2)} ${series.unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            minUnit,
            displayFormats: { millisecond: 'HH:mm', second: 'HH:mm', minute: 'HH:mm', hour: 'HH:mm', day: 'dd.MM.' },
            tooltipFormat: 'dd.MM.yyyy HH:mm',
          },
          adapters: { date: { locale: de } },
          grid: { display: false },
          ticks: { maxTicksLimit: 10, font: { size: 9 }, callback: dateAwareTickCallback },
        },
        y: {
          grid: { color: 'rgba(0, 0, 0, 0.05)' },
          ticks: { font: { size: 9 }, callback: (v: string | number) => `${typeof v === 'number' ? fmt(v, v % 1 === 0 ? 0 : 1) : v} ${series.unit}` },
        },
      },
    }),
    [data, series.label, series.unit, minUnit]
  );

  return (
    <div className="mb-6">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{title}</h4>
      <div className="h-48 bg-gray-50 dark:bg-primary-700 rounded-lg p-2">
        <Line data={data} options={options} plugins={[markerPlugin]} />
      </div>
    </div>
  );
};

/**
 * Historical loading/power/voltage charts for a network element, fed from the
 * powerflow snapshot archive (same data source as the map time-rewind).
 */
export const ElementHistoryModal: React.FC<Props> = ({
  isOpen,
  elementId,
  elementName,
  showLoading = false,
  showVoltage = true,
  showMeasuredTrafo = false,
  onClose,
}) => {
  const [rangeKey, setRangeKey] = useState<RangeKey>('30d');
  const [points, setPoints] = useState<ElementSeriesPoint[] | null>(null);
  const [measuredPoints, setMeasuredPoints] = useState<MeasuredTrafoSeriesPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Instant currently shown on the map (time-rewind) — drawn as a marker line.
  const playbackTs = useHistoricalPlaybackStore(state => state.effectiveTs);
  const playbackMode = useHistoricalPlaybackStore(state => state.mode);
  const markerMs = playbackMode === 'historical' && playbackTs ? parseUtcTimestampMs(playbackTs) : null;

  // Escape closes the modal.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // A different element got selected while the modal was open (search,
  // marker click): the modal belongs to the old element - close it.
  const openedForRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) { openedForRef.current = null; return; }
    if (openedForRef.current == null) { openedForRef.current = elementId; return; }
    if (openedForRef.current !== elementId) { onClose(); }
  }, [isOpen, elementId, onClose]);

  useEffect(() => {
    if (!isOpen || !elementId) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const range = await fetchSnapshotRange();
        if (cancelled) return;
        if (!range || range.count === 0 || range.from == null || range.to == null) {
          setPoints([]);
          setMeasuredPoints([]);
          return;
        }
        const toMs = parseUtcTimestampMs(range.to);
        const fromMs =
          rangeKey === 'all'
            ? parseUtcTimestampMs(range.from)
            : Math.max(parseUtcTimestampMs(range.from), toMs - RANGE_DAYS[rangeKey] * 24 * 60 * 60 * 1000);
        // Measured series may reach further back than the computed archive
        // (e.g. the 51.7 Hz curtailment predates the meter window) — widen
        // the "Gesamt" window for the measured fetch.
        const measuredFromMs = rangeKey === 'all' ? fromMs - 90 * 24 * 60 * 60 * 1000 : fromMs;
        const [data, measured] = await Promise.all([
          fetchElementSeries(elementId, new Date(fromMs).toISOString(), new Date(toMs).toISOString()),
          showMeasuredTrafo
            ? fetchMeasuredTrafoSeries(elementId, new Date(measuredFromMs).toISOString(), new Date(toMs).toISOString())
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setPoints(data.points ?? []);
        setMeasuredPoints(measured?.points ?? []);
      } catch (err) {
        console.error('[ElementHistory] fetch failed:', err);
        if (!cancelled) setError('Historische Daten konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, elementId, rangeKey, showMeasuredTrafo]);

  const timestamps = useMemo(() => (points ?? []).map(p => p.t), [points]);
  const measuredTimestamps = useMemo(() => measuredPoints.map(p => p.t), [measuredPoints]);

  if (!isOpen) return null;

  const hasPoints = points != null && points.length > 0;
  const hasLoading = hasPoints && points.some(p => p.loadingAvg != null);
  const hasVoltage = hasPoints && points.some(p => p.voltageAvgPu != null);
  const hasMeasured = measuredPoints.length > 0;
  const hasMeasuredFreq = hasMeasured && measuredPoints.some(p => p.freqAvg != null);
  const hasMeasuredU = hasMeasured && measuredPoints.some(p => p.uAvgV != null);
  const hasMeasuredI = hasMeasured && measuredPoints.some(p => p.iAvgA != null);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-9999">
      <div className="bg-white dark:bg-primary-800 rounded-xl shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 bg-linear-to-r from-accent-primary/10 dark:from-accent-primary/20 to-white dark:to-primary-800 border-b border-gray-300 dark:border-primary-600 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-primary-50">Lastfluss-Historie (Archiv)</h3>
            <p className="text-xs text-gray-500 dark:text-primary-400">
              {elementName} · berechnete Werte aus gespeicherten Lastfluss-Ergebnissen
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-primary-700 rounded-lg transition-colors"
            aria-label="Schließen"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-200 dark:border-primary-600 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 dark:text-gray-400">Zeitraum:</span>
          {(Object.keys(RANGE_LABELS) as RangeKey[]).map(key => (
            <button
              key={key}
              onClick={() => setRangeKey(key)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                rangeKey === key
                  ? 'bg-accent-primary text-white'
                  : 'bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-primary-600'
              }`}
            >
              {RANGE_LABELS[key]}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
            </div>
          )}

          {error && (
            <div className="bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400 p-4 rounded-lg">{error}</div>
          )}

          {!isLoading && !error && !hasPoints && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p>Keine archivierten Lastfluss-Ergebnisse für dieses Element.</p>
            </div>
          )}

          {!isLoading && !error && hasPoints && (
            <div>
              {showLoading && hasLoading && (
                <BandChart
                  title="Auslastung"
                  timestamps={timestamps}
                  markerMs={markerMs}
                  series={{
                    label: 'Auslastung',
                    unit: '%',
                    min: points.map(p => p.loadingAvg),
                    avg: points.map(p => p.loadingAvg),
                    max: points.map(p => p.loadingMax),
                    color: { border: 'rgb(34, 197, 94)', bg: 'rgba(34, 197, 94, 0.12)' },
                  }}
                />
              )}
              <BandChart
                title="Wirkleistung"
                timestamps={timestamps}
                markerMs={markerMs}
                series={{
                  label: 'Wirkleistung',
                  unit: 'kW',
                  min: points.map(p => p.powerMinKw),
                  avg: points.map(p => p.powerAvgKw),
                  max: points.map(p => p.powerMaxKw),
                  color: { border: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.12)' },
                }}
              />
              {showVoltage && hasVoltage && (
                <BandChart
                  title="Spannung (L-N, berechnet)"
                  timestamps={timestamps}
                  markerMs={markerMs}
                  series={{
                    label: 'Spannung',
                    unit: 'V',
                    min: points.map(p => (p.voltageMinPu != null ? p.voltageMinPu * PU_BASE_LN_V : null)),
                    avg: points.map(p => (p.voltageAvgPu != null ? p.voltageAvgPu * PU_BASE_LN_V : null)),
                    max: points.map(p => (p.voltageMaxPu != null ? p.voltageMaxPu * PU_BASE_LN_V : null)),
                    color: { border: 'rgb(139, 92, 246)', bg: 'rgba(139, 92, 246, 0.12)' },
                    limits: [
                      { value: VOLTAGE_OPERATIONAL_MIN_V, label: `Untergrenze ${VOLTAGE_OPERATIONAL_MIN_V.toLocaleString('de-DE', { maximumFractionDigits: 1 })} V` },
                      { value: VOLTAGE_OPERATIONAL_MAX_V, label: `Obergrenze ${VOLTAGE_OPERATIONAL_MAX_V.toLocaleString('de-DE', { maximumFractionDigits: 1 })} V` },
                    ],
                  }}
                />
              )}
              {showMeasuredTrafo && hasMeasuredFreq && (
                <BandChart
                  title="Frequenz (gemessen)"
                  timestamps={measuredTimestamps}
                  markerMs={markerMs}
                  series={{
                    label: 'Frequenz',
                    unit: 'Hz',
                    min: measuredPoints.map(p => p.freqMin),
                    avg: measuredPoints.map(p => p.freqAvg),
                    max: measuredPoints.map(p => p.freqMax),
                    color: { border: 'rgb(234, 179, 8)', bg: 'rgba(234, 179, 8, 0.12)' },
                    limits: [
                      { value: 49.8, label: '49,8 Hz' },
                      { value: 50.2, label: '50,2 Hz' },
                    ],
                  }}
                />
              )}
              {showMeasuredTrafo && hasMeasuredU && (
                <BandChart
                  title="Spannung (gemessen, L-N)"
                  timestamps={measuredTimestamps}
                  markerMs={markerMs}
                  series={{
                    label: 'Spannung gemessen',
                    unit: 'V',
                    min: measuredPoints.map(p => p.uMinV),
                    avg: measuredPoints.map(p => p.uAvgV),
                    max: measuredPoints.map(p => p.uMaxV),
                    color: { border: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.12)' },
                    limits: [
                      { value: VOLTAGE_OPERATIONAL_MIN_V, label: `Untergrenze ${VOLTAGE_OPERATIONAL_MIN_V.toLocaleString('de-DE', { maximumFractionDigits: 1 })} V` },
                      { value: VOLTAGE_OPERATIONAL_MAX_V, label: `Obergrenze ${VOLTAGE_OPERATIONAL_MAX_V.toLocaleString('de-DE', { maximumFractionDigits: 1 })} V` },
                    ],
                  }}
                />
              )}
              {showMeasuredTrafo && hasMeasuredI && (
                <BandChart
                  title="Strom (gemessen, max. Phase)"
                  timestamps={measuredTimestamps}
                  markerMs={markerMs}
                  series={{
                    label: 'Strom gemessen',
                    unit: 'A',
                    min: measuredPoints.map(p => p.iAvgA),
                    avg: measuredPoints.map(p => p.iAvgA),
                    max: measuredPoints.map(p => p.iMaxA),
                    color: { border: 'rgb(20, 184, 166)', bg: 'rgba(20, 184, 166, 0.12)' },
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-primary-600 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-primary-600 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ElementHistoryModal;
